import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import AuthForm from './AuthForm';
import ProfileCompletionModal from './ProfileCompletionModal';
import { useProfileCompletion } from '@/hooks/useProfileCompletion';

interface AuthModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAuthSuccess: () => void;
  message?: string;
  /**
   * Overrides the default pre-Google-OAuth hook (which stores the legacy
   * upload pending-submit flag). Flows with their own pending intent pass
   * their own hook so the default never clobbers it.
   */
  onBeforeGoogleAuth?: () => void;
}

const AuthModal: React.FC<AuthModalProps> = ({
  open,
  onOpenChange,
  onAuthSuccess,
  message = 'Sign in or create an account to publish your paper',
  onBeforeGoogleAuth,
}) => {
  const { user } = useAuth();
  const { needsProfileCompletion, checkProfile } = useProfileCompletion();
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [authCompleted, setAuthCompleted] = useState(false);

  // When user becomes authenticated, check if profile needs completion
  useEffect(() => {
    const handleAuth = async () => {
      if (user && open && !authCompleted) {
        setAuthCompleted(true);
        onOpenChange(false);
        
        // Check if profile is incomplete
        const needsCompletion = await checkProfile();
        if (needsCompletion) {
          setShowProfileModal(true);
        } else {
          onAuthSuccess();
        }
      }
    };
    
    handleAuth();
  }, [user, open, authCompleted, checkProfile, onAuthSuccess, onOpenChange]);

  // Reset auth completed state when modal opens
  useEffect(() => {
    if (open) {
      setAuthCompleted(false);
    }
  }, [open]);

  const handleBeforeGoogleAuth = () => {
    if (onBeforeGoogleAuth) {
      onBeforeGoogleAuth();
      return;
    }
    // Store pending submit flag so we know to auto-submit after auth
    sessionStorage.setItem('pendingUploadSubmit', 'true');
  };

  const handleProfileComplete = () => {
    setShowProfileModal(false);
    onAuthSuccess();
  };

  const handleProfileSkip = () => {
    setShowProfileModal(false);
    onAuthSuccess();
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl text-center">
              Welcome to PaperVerse
            </DialogTitle>
            <DialogDescription className="text-center">
              {message}
            </DialogDescription>
          </DialogHeader>
          
          <AuthForm 
            defaultMode="signin"
            onAuthSuccess={onAuthSuccess}
            showForgotPassword={true}
            compact={true}
            onBeforeGoogleAuth={handleBeforeGoogleAuth}
          />
        </DialogContent>
      </Dialog>

      <ProfileCompletionModal
        open={showProfileModal}
        onOpenChange={setShowProfileModal}
        onComplete={handleProfileComplete}
        onSkip={handleProfileSkip}
      />
    </>
  );
};

export default AuthModal;
